import React, { useState } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/fetchWithAuth';

export interface Organization {
  id: string;
  name: string;
  code: string;
  logo?: string;
  type: 'organization' | 'region' | 'location';
}

interface OrganizationSwitcherProps {
  currentOrganizationId?: string;
  onOrganizationChange?: (organizationId: string) => void;
  className?: string;
}

export function OrganizationSwitcher({
  currentOrganizationId,
  onOrganizationChange,
  className
}: OrganizationSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Fetch all organizations the user has access to
  const { data: organizations = [], isLoading } = useQuery<Organization[]>({
    queryKey: ['user-organizations'],
    queryFn: async () => {
      const response = await apiRequest<Organization[]>('/api/organizations/user-access');
      return response || [];
    },
  });

  // Find current organization
  const currentOrg = organizations.find(org => org.id === currentOrganizationId);

  const handleOrganizationSelect = (orgId: string) => {
    if (orgId !== currentOrganizationId && onOrganizationChange) {
      onOrganizationChange(orgId);
    }
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center space-x-2 px-3 py-2", className)}>
        <Building2 className="h-5 w-5 text-muted-foreground animate-pulse" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (!organizations.length) {
    return null;
  }


  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex items-center justify-between space-x-2 px-3 py-2 h-auto",
            "hover:bg-accent hover:text-accent-foreground",
            "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
            className
          )}
          aria-label="organization switcher"
          data-testid="org-switcher"
        >
          <div className="flex min-w-0 items-center space-x-2">
            {currentOrg?.logo ? (
              <img 
                src={currentOrg.logo} 
                alt={currentOrg.name} 
                className="h-6 w-6 rounded"
              />
            ) : (
              <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
            )}
            <span className="font-medium text-sm truncate">{currentOrg?.name || 'Select organization'}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px]">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => handleOrganizationSelect(org.id)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex min-w-0 items-center space-x-2">
              {org.logo ? (
                <img 
                  src={org.logo} 
                  alt={org.name} 
                  className="h-5 w-5 rounded"
                />
              ) : (
                <div className="h-5 w-5 rounded bg-muted flex items-center justify-center">
                  <Building2 className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-sm">{org.name}</span>
                {org.code && (
                  <span className="text-xs text-muted-foreground">{org.code}</span>
                )}
              </div>
            </div>
            {org.id === currentOrganizationId && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 